import { Dropdown, Icon, IconButton, TextField, TooltipHost } from '@fluentui/react';
import { Field, FieldArray, FieldProps, useFormikContext } from 'formik';
import { FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IActionUrlForm, ParameterValues } from '../../types/ActionUrl';
import { getFieldMetaError } from '../../../../error/utils/getFieldMetaError';

import styles from '../../styles/ActionUrl.module.css';

interface ParameterItemProps {
  index: number;
  remove: (inx: number) => void;
}

interface ParameterOption {
  key: ParameterValues;
  text: string;
}

export const ParameterItem: FC<ParameterItemProps> = ({ index, remove }) => {
  const { t } = useTranslation();
  const [isCustomParameterVisible, setIsCustomParameterVisible] = useState(false);
  const { values, errors, setFieldValue, getFieldMeta } = useFormikContext<IActionUrlForm>();
  const { value: parameterValue } = getFieldMeta<string | ParameterValues>(`parameters.${index}.parameterValue`);

  const paramsList = Object.values(ParameterValues);

  const getParameterValue = (value: ParameterValues | undefined) => {
    if (value === undefined) {
      return '';
    }

    if (paramsList.indexOf(value) === -1) {
      return ParameterValues.custom;
    }

    return value;
  };

  const renderParameterValidationError = () => {
    const paramError = errors.parameters?.[index];

    if (typeof paramError !== 'string') {
      return null;
    }

    return (
      <div className={styles.parameterValidationError}>
        {paramError}
      </div>
    );
  };

  const selectedParameterKeys = values.parameters.map((p) => p.parameterValue);

  const filterSelectedParameters= (parameter: ParameterOption, currentIndex: number) => {
    const selectedParameters = selectedParameterKeys
      .filter((_, i) => i !== currentIndex);

    return !selectedParameters.includes(parameter.key);
  };

  useEffect(() => {
    const customParameterIndex = paramsList.indexOf(ParameterValues.custom);
    const parameterValueIndex = paramsList.indexOf(parameterValue as ParameterValues);

    if (parameterValue === undefined) {
      setIsCustomParameterVisible(false);
      return;
    }

    if (parameterValueIndex === -1 || parameterValueIndex === customParameterIndex) {
      setIsCustomParameterVisible(true);
    } else {
      setIsCustomParameterVisible(false);
    }
  }, [parameterValue, paramsList]);

  return (
    <Fragment>
      <div className={styles.parameterItemWrapper}>
        <div style={{ width: '50%' }}>
          <Field name={`parameters.${index}.parameterName`}>
            {({ field, meta }: FieldProps<string>) => (
              <TextField
                label={t("common:parameterName")}
                errorMessage={meta.error}
                {...field}
              />
            )}
          </Field>
        </div>
        <div className={styles.customParameterWrapper}>
          <div style={{ width: '100%' }}>
            <Field name={`parameters.${index}.parameterValue`}>
              {({ field, meta }: FieldProps<ParameterValues>) => (
                <>
                  <Dropdown
                    placeholder={t('common:selectValue')}
                    label={t('common:parameterValue')}
                    selectedKey={getParameterValue(field.value)}
                    onChange={(_, option) => {
                      if (option?.key === ParameterValues.custom) {
                        return setFieldValue(`parameters.${index}.parameterValue`, '');
                      }

                      void setFieldValue(`parameters.${index}.parameterValue`, option?.key);
                    }}
                    options={[
                      { key: ParameterValues.caller_number, text: t('common:callerNumber') },
                      { key: ParameterValues.attendant_name, text: t('common:attendantName') },
                      { key: ParameterValues.attendant_extension, text: t('common:attendantExtension') },
                      { key: ParameterValues.attendant_email, text: t('common:attendantEmail') },
                      { key: ParameterValues.custom, text: t('common:custom') },
                    ].filter((parameter) => filterSelectedParameters(parameter, index))}
                    errorMessage={getFieldMetaError(meta)}
                  />
                  {isCustomParameterVisible && (
                    <div style={{ marginTop: 4 }}>
                      <TextField
                        placeholder={t('common:enterCustomValue')}
                        required
                        errorMessage={getFieldMetaError(meta)}
                        {...field}
                      />
                    </div>
                  )}

                  {parameterValue === ParameterValues.caller_number && (
                    <FieldArray name={`parameters.${index}.numberReplacement`}>
                      {({ push, remove }) => (
                        <div style={{ marginTop: 8 }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 4
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <label style={{ fontSize: 13 }}>{t('common:numberMapping')}</label>

                              <TooltipHost
                                content={t('common:numberMappingInfo')}
                                id="numberMapping"
                              >
                                <Icon iconName="info" style={{ cursor: 'pointer' }} />
                              </TooltipHost>
                            </div>
                            <div style={{ marginTop: 8 }}>
                              <IconButton
                                style={{ height: 18, width: 18 }}
                                iconProps={{ iconName: 'Add' }}
                                aria-label={t('common:addRow')}
                                onClick={() => push(['', ''])}
                              />
                            </div>
                          </div>

                          {values.parameters[index].numberReplacement?.map((pair, pairIndex) => (
                            <div key={pairIndex} style={{ display: 'flex', gap: 8, marginTop: 4, alignItems: 'flex-end' }}>
                              <Field name={`parameters.${index}.numberReplacement.${pairIndex}.0`}>
                                {({ field }: FieldProps<string>) => (
                                  <TextField
                                    label={t('common:from')}
                                    placeholder={'+31'}
                                    required
                                    {...field}
                                  />
                                )}
                              </Field>
                              <Field name={`parameters.${index}.numberReplacement.${pairIndex}.1`}>
                                {({ field }: FieldProps<string>) => (
                                  <TextField
                                    label={t('common:to')}
                                    placeholder={'0'}
                                    required
                                    {...field}
                                  />
                                )}
                              </Field>
                              <IconButton
                                iconProps={{ iconName: 'Delete' }}
                                aria-label={t('common:removeRow')}
                                onClick={() => remove(pairIndex)}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </FieldArray>
                  )}
                </>
              )}
            </Field>
          </div>
          {values.parameters.length > 1 && (
            <div className={styles.flexEndContainer}>
              <IconButton
                style={{ marginTop: 28 }}
                iconProps={{ iconName: 'Delete' }}
                aria-label={t('common:removeParameter')}
                onClick={() => remove(index)}
              />
            </div>
          )}
        </div>
      </div>
      {renderParameterValidationError()}
    </Fragment>
  )
};